import { useEffect, useReducer } from 'react';
import {
  QuestionnaireGroupResponse,
  QuestionnaireResponse,
  SelectionRequest,
  StorePreferenceCreateRequest,
} from '@tiendanube/common';
import {
  trackingQuestionnaireContinueClick,
  trackingQuestionnaireResponse,
  trackingQuestionnaireReturnClick,
  trackingQuestionnaireSkipClick,
} from 'App/tracking';
import { useStorage } from 'commons/hooks';
import { useGetStoreInfo } from 'domains/Auth/hooks';
import useTranslationPreOnboarding from 'domains/PreOnboarding/useTranslationPreOnboarding';
import { FINISH_MARKER } from './constants';
import { getFilteredAnswersTypes } from './getFilteredAnswersTypes';
import { getNextStepCategory } from './getNextStepCategory';
import { getNextStepIndex } from './getNextStepIndex';
import formReducer from './questionnaireReducer';

interface UseQuestionnaireLogicParams {
  response: QuestionnaireResponse;
  onCompleted: (payload: StorePreferenceCreateRequest) => Promise<void>;
}

function removeCountrySuffix(groupKey: string): string {
  return groupKey.replace(/_(ar|br|mx|cl|co)$/, '');
}

function checkIfSelectedEcommerce(
  answers: Record<string, SelectionRequest[]>,
  response: QuestionnaireResponse,
): boolean {
  const salesStatusKey = Object.keys(answers).find((key) =>
    key.startsWith('sales_status'),
  );
  if (!salesStatusKey) return false;

  const salesStatusGroup = response.options.find(
    (group) => group.group === salesStatusKey,
  );
  if (!salesStatusGroup) return false;

  const salesStatusAnswers = answers[salesStatusKey] ?? [];
  return salesStatusAnswers.some((answer) => {
    const option = salesStatusGroup.types.find(
      (opt) => opt.id === answer.domain_mapping_id,
    );
    return option?.code === 'already_selling_e-commerce';
  });
}

function getCurrentStepInfo(
  response: QuestionnaireResponse,
  currentStep: number,
  answers: Record<string, SelectionRequest[]>,
) {
  const currentGroup: QuestionnaireGroupResponse | undefined =
    response.options[currentStep];
  const currentValues = currentGroup ? answers[currentGroup.group] ?? [] : [];
  const isCheckbox = currentGroup?.isCheckbox ?? false;
  const isLastStep =
    currentGroup?.jumpToOn?.default === FINISH_MARKER ||
    currentStep === response.options.length - 1;
  return { currentGroup, currentValues, isCheckbox, isLastStep };
}

const PRE_ONBOARDING_QUESTIONNAIRE_ANSWERS =
  'pre-onboarding-questionnaire-answers';

/**
 * Track all questionnaire responses
 */
function trackQuestionnaireAnswers(
  allAnswers: Record<string, SelectionRequest[]>,
  response: QuestionnaireResponse,
): void {
  for (const [groupKey, selections] of Object.entries(allAnswers)) {
    for (const selection of selections) {
      const option = response.options
        .find((opt) => opt.group === groupKey)
        ?.types.find((type) => type.id === selection.domain_mapping_id);

      if (option) {
        // Include free_text if it exists
        trackingQuestionnaireResponse(
          groupKey,
          option.code,
          selection.free_text,
        );
      }
    }
  }
}

export function useQuestionnaireLogic({
  response,
  onCompleted,
}: UseQuestionnaireLogicParams) {
  const { id, country } = useGetStoreInfo();
  const [questionnaireAnswers, setQuestionnaireAnswers, loaded] = useStorage(
    `${PRE_ONBOARDING_QUESTIONNAIRE_ANSWERS}-${id}`,
    '',
  );
  const [state, dispatch] = useReducer(formReducer, {
    currentStep: 0,
    answers: {},
    currentStepCategory: [],
    stepHistory: [{ stepIndex: 0, category: [] }],
  });
  const t = useTranslationPreOnboarding();

  useEffect(() => {
    if (loaded && questionnaireAnswers) {
      const parsedAnswers = JSON.parse(questionnaireAnswers) as Record<
        string,
        SelectionRequest[]
      >;
      for (const [groupKey, values] of Object.entries(parsedAnswers)) {
        dispatch({
          type: 'ANSWER_GROUP',
          payload: { groupKey, values },
        });
      }
    }
  }, [loaded, questionnaireAnswers]);

  const { currentGroup, currentValues, isCheckbox, isLastStep } =
    getCurrentStepInfo(response, state.currentStep, state.answers);

  const clearFollowingAnswers = (startingFrom: string) => {
    const index = response.options.findIndex(
      (step) => step.group === startingFrom,
    );
    const groupsToClear = response.options
      .map((step) => step.group)
      .filter((_, i) => i !== state.currentStep && i >= index);

    for (const group of groupsToClear) {
      dispatch({
        type: 'ANSWER_GROUP',
        payload: { groupKey: group, values: [] },
      });
    }
  };

  const setValues = (values: SelectionRequest[]) => {
    if (!currentGroup) return;

    const groupKey = currentGroup.group;
    const nextAnswer = values[0];

    const shouldResetAll = groupKey.startsWith('sales_status_') && nextAnswer;

    if (shouldResetAll) {
      clearFollowingAnswers(groupKey);
      dispatch({
        type: 'ANSWER_GROUP',
        payload: { groupKey, values },
      });
    } else {
      dispatch({ type: 'ANSWER_GROUP', payload: { groupKey, values } });
    }
  };

  const handleNextStep = (newValues: SelectionRequest[], fromSkip: boolean) => {
    if (!currentGroup) return;

    if (!fromSkip) {
      trackingQuestionnaireContinueClick(
        removeCountrySuffix(currentGroup.group),
      );
    }

    const updatedAnswers = {
      ...state.answers,
      [currentGroup.group]: newValues,
    };

    dispatch({
      type: 'ANSWER_GROUP',
      payload: { groupKey: currentGroup.group, values: newValues },
    });

    const nextStep = getNextStepIndex({
      currentStep: state.currentStep,
      response,
      currentGroup,
      currentValues: newValues,
      allAnswers: updatedAnswers,
    });
    const groupCategory = getNextStepCategory({
      currentStep: state.currentStep,
      nextStep,
      currentGroup,
      currentValues: newValues,
    });

    setQuestionnaireAnswers(JSON.stringify(updatedAnswers));

    if (nextStep < response.options.length) {
      dispatch({
        type: 'SET_STEP_WITH_HISTORY',
        payload: {
          stepIndex: nextStep,
          groupCategory: groupCategory,
        },
      });
    } else {
      const allAnswers = {
        ...state.answers,
        [currentGroup.group]: newValues,
      };

      const payload = Object.values(allAnswers).flat();

      // Track each questionnaire response
      trackQuestionnaireAnswers(allAnswers, response);

      onCompleted({ selections: payload, country }).then(() => {
        setQuestionnaireAnswers('');
      });
    }
  };

  const handleSubmit = () => handleNextStep(currentValues, false);

  const handleSkipGroup = (groupKey: string, skipId: number) => {
    clearFollowingAnswers(groupKey);
    trackingQuestionnaireSkipClick(removeCountrySuffix(groupKey));
    handleNextStep([{ domain_mapping_id: skipId }], true);
  };

  const handlePreviousStep = () => {
    if (state.stepHistory.length < 2 || !currentGroup) return;
    trackingQuestionnaireReturnClick(removeCountrySuffix(currentGroup.group));
    dispatch({ type: 'PREV_STEP' });
  };

  return {
    state,
    dispatch,
    currentGroup,
    currentValues,
    isCheckbox,
    isLastStep,
    transformedTypes: currentGroup?.types
      ? getFilteredAnswersTypes({
          currentGroup,
          currentCategory: state.currentStepCategory,
          t,
        })
      : [],
    hasSelectedEcommerce: checkIfSelectedEcommerce(state.answers, response),
    setValues,
    handleSubmit,
    handleSkipGroup,
    handlePreviousStep,
  };
}
